import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { Select } from "@components/forms/select";
import { Pagination } from "@components/pagination";
import { IRaffleCategoryDTO } from "@hooks/dtos/raffles/IRaffleCategoryDTO";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  getRaffleStatus,
  raffleStatusSelect,
} from "@hooks/functions/Raffle/status";
import { getRaffleType } from "@hooks/functions/Raffle/type";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import {
  FaBroom,
  FaChartSimple,
  FaCircleInfo,
  FaExpand,
  FaEye,
  FaMinus,
  FaTicket,
} from "react-icons/fa6";
import { FaTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

import { CampaignDescriptionModal } from "./components/CampaignDescriptionModal";
import { CampaignDetailsModal } from "./components/CampaignDetailsModal";
import { CampaignTicketsModal } from "./components/CampaignTicketsModal";
import { ThumbLightbox } from "./components/ThumbLightbox";
import {
  ITicketCampaign,
  ITicketCampaignTicket,
  ITicketFilters,
} from "./types";

const masks = new Masks();

const campaignStatusOptions = [
  { value: "", name: "Todos" },
  ...raffleStatusSelect.map((status) => ({
    value: status.status,
    name: status.text,
  })),
];

const takeOptions = [
  { value: 10, name: "10 registros" },
  { value: 25, name: "25 registros" },
  { value: 50, name: "50 registros" },
  { value: 100, name: "100 registros" },
];

const orderOptions = [
  { value: "DESC", name: "Recentes" },
  { value: "ASC", name: "Antigas" },
];

function getProgressVariant(progress: number) {
  if (progress >= 100) return "secondary";
  if (progress >= 70) return "success";
  if (progress >= 50) return "primary";
  return "warning";
}

interface ICampaignListOverride {
  page?: number;
  status?: RaffleStatusTypes | "";
  categories?: number[];
  searchCampaign?: string;
  startAt?: string;
  endAt?: string;
}

interface ITicketListOverride {
  page?: number;
  take?: number;
  order?: "DESC" | "ASC";
}

export function MyTickets() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [show, setShow] = useState({ filter: true });
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [searchCampaign, setSearchCampaign] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [filters, setFilters] = useState<ITicketFilters>({
    status: "",
  });
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [order, setOrder] = useState<"DESC" | "ASC">("DESC");
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [campaigns, setCampaigns] = useState<ITicketCampaign[]>([]);
  const [isLoading, setIsLoading] = useState({
    campaigns: true,
    tickets: false,
  });
  const [selectedDescription, setSelectedDescription] =
    useState<ITicketCampaign>();
  const [selectedThumb, setSelectedThumb] = useState<ITicketCampaign>();
  const [selectedDetails, setSelectedDetails] = useState<ITicketCampaign>();
  const [selectedTicketsCampaign, setSelectedTicketsCampaign] =
    useState<ITicketCampaign>();
  const [campaignTickets, setCampaignTickets] = useState<
    ITicketCampaignTicket[]
  >([]);
  const [totalTickets, setTotalTickets] = useState<number>(0);
  const [totalTicketPages, setTotalTicketPages] = useState<number>(1);
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTake, setTicketTake] = useState(10);
  const [ticketOrder, setTicketOrder] = useState<"DESC" | "ASC">("DESC");
  const [categories, setCategories] = useState<IRaffleCategoryDTO[]>([]);

  function handleSelectCategory(category: number) {
    setSelectedCategories((prev) => {
      const categories = prev.includes(category)
        ? prev.filter((currentCategory) => currentCategory !== category)
        : [...prev, category];

      setPage(1);
      handleCampaignList({ page: 1, categories });

      return categories;
    });
  }

  function cleanFilters() {
    setSelectedCategories([]);
    setSearchCampaign("");
    setStartAt("");
    setEndAt("");
    setFilters({ status: "" });
    setPage(1);
    handleCampaignList({
      page: 1,
      status: "",
      categories: [],
      searchCampaign: "",
      startAt: "",
      endAt: "",
    });
  }

  async function handleCampaignList(override?: ICampaignListOverride) {
    setIsLoading((prev) => ({ ...prev, campaigns: true }));

    try {
      const params = {
        page: override?.page ?? page,
        take,
        order,
        status: override?.status ?? filters.status,
        categories: override?.categories ?? selectedCategories,
        searchCampaign: override?.searchCampaign ?? searchCampaign,
        startAt: override?.startAt ?? startAt,
        endAt: override?.endAt ?? endAt,
      };
      const result = await api.get(`/raffleClient/default/tickets/campaigns`, {
        params,
      });
      const nextCampaigns =
        result.data?.result ||
        result.data?.list ||
        result.data?.campaigns ||
        [];
      const nextTotal = Number(result.data?.total || nextCampaigns.length);

      setCampaigns(Array.isArray(nextCampaigns) ? nextCampaigns : []);
      setTotal(nextTotal);
      setTotalPages(Math.ceil(nextTotal / take) || 1);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data?.message ??
          "Falha ao carregar campanhas com tickets pagos.",
        type: "error",
        theme: "dark",
      });
      setCampaigns([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setIsLoading((prev) => ({ ...prev, campaigns: false }));
    }
  }

  const handleCategoryList = async () => {
    try {
      const params = `?`;
      const result = await api.get(`/raffle/categories${params}`);

      setCategories(result.data);
    } catch (err: any) {
      console.error(err);
    }
  };

  function handleCampaignTickets(campaign: ITicketCampaign) {
    setTicketPage(1);
    setTicketTake(10);
    setTicketOrder("DESC");
    setCampaignTickets([]);
    setTotalTickets(0);
    setTotalTicketPages(1);
    setSelectedTicketsCampaign(campaign);
  }

  async function loadCampaignTickets(
    campaign: ITicketCampaign,
    override?: ITicketListOverride,
  ) {
    setIsLoading((prev) => ({ ...prev, tickets: true }));

    try {
      const result = await api.get(
        `/raffleClient/default/tickets/campaign/${campaign.key}`,
        {
          params: {
            page: override?.page ?? ticketPage,
            take: override?.take ?? ticketTake,
            order: override?.order ?? ticketOrder,
          },
        },
      );

      const tickets =
        result.data?.list || result.data?.result || result.data?.tickets || [];
      const nextTotal = Number(result?.data?.total || tickets.length);
      const nextTake = override?.take ?? ticketTake;

      setTotalTickets(nextTotal);
      setTotalTicketPages(
        Number(result?.data?.totalPages) || Math.ceil(nextTotal / nextTake) || 1,
      );
      setCampaignTickets(Array.isArray(tickets) ? tickets : []);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data?.message ??
          "Falha ao carregar tickets da campanha.",
        type: "error",
        theme: "dark",
      });
      setCampaignTickets([]);
      setTotalTickets(0);
      setTotalTicketPages(1);
    } finally {
      setIsLoading((prev) => ({ ...prev, tickets: false }));
    }
  }

  useEffect(() => {
    handleCategoryList();
  }, []);

  useEffect(() => {
    handleCampaignList();
  }, [page, take, order]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (selectedTicketsCampaign) {
      loadCampaignTickets(selectedTicketsCampaign);
    }
  }, [selectedTicketsCampaign, ticketPage, ticketTake, ticketOrder]);

  useEffect(() => {
    if (ticketPage > totalTicketPages) {
      setTicketPage(totalTicketPages);
    }
  }, [ticketPage, totalTicketPages]);

  return (
    <>
      <section className="bg-light pt-5 pb-4 px-3 overflow-hidden">
        <div className="container px-3 px-md-4 px-lg-0">
          <div className="mb-4">
            <h1 className="h3 fw-bold mb-1">Meus Tickets</h1>
            <p className="text-muted mb-0">
              Campanhas nas quais você possui tickets com pagamento confirmado.
            </p>
          </div>

          <div className="row">
            <div className="col-12">
              <Card>
                <button
                  type="button"
                  className="card-header border-0 text-start w-100"
                  onClick={() =>
                    setShow((prev) => ({ ...prev, filter: !prev.filter }))
                  }
                >
                  <div className="d-flex justify-content-between gap-3">
                    <div>
                      <div className="card-title">Filtrar Campanhas</div>
                      <div className="card-category">
                        Utilize o filtro para consultar suas campanhas com
                        tickets pagos.
                      </div>
                    </div>
                    <div>
                      {show.filter ? (
                        <FaMinus
                          className="fs-5"
                          title="Clique para ocultar as informações."
                        />
                      ) : (
                        <FaExpand
                          className="fs-5"
                          title="Clique para visualizar as informações."
                        />
                      )}
                    </div>
                  </div>
                </button>

                <div className={`card-body ${show.filter ? "" : "d-none"}`}>
                  <div className="d-flex flex-wrap mb-4 pb-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategories([]);
                        setPage(1);
                        handleCampaignList({ page: 1, categories: [] });
                      }}
                      className="btn btn-sm btn-outline-danger rounded-pill"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <FaTrashAlt /> Limpar
                    </button>
                    {categories.map((category) => (
                      <button
                        type="button"
                        key={category.id}
                        onClick={() => handleSelectCategory(category.id)}
                        className={`btn btn-sm rounded-pill ${
                          selectedCategories.includes(category.id)
                            ? "btn-warning text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-lg-3">
                      <Input
                        animated
                        label="Nome da Campanha"
                        placeholder="Digite nome, slug ou key"
                        type="text"
                        value={searchCampaign}
                        onChange={(e) => {
                          setSearchCampaign(e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12 col-lg-3">
                      <Select
                        animated
                        label="Status da Campanha"
                        className="form-select form-control"
                        aria-label="status"
                        value={filters.status}
                        options={campaignStatusOptions}
                        onChange={(e) => {
                          setFilters((prev) => ({
                            ...prev,
                            status: e.target.value as RaffleStatusTypes | "",
                          }));
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <Input
                        animated
                        id="startAt"
                        label="Data Inicial"
                        placeholder=""
                        type="date"
                        value={startAt}
                        onChange={(e) => {
                          setStartAt(e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <Input
                        animated
                        id="endAt"
                        label="Data Final"
                        placeholder=""
                        type="date"
                        value={endAt}
                        onChange={(e) => {
                          setEndAt(e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12">
                      <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary px-4 order-2 order-sm-1"
                          onClick={cleanFilters}
                          title="Limpar todos os filtros."
                        >
                          <FaBroom className="me-2" />
                          Limpar
                        </button>
                        <button
                          type="button"
                          className="btn btn-success px-5 order-1 order-sm-2"
                          onClick={() => {
                            setPage(1);
                            handleCampaignList({ page: 1 });
                          }}
                        >
                          Filtrar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <Card className="mt-4">
            <div className="card-header">
              <div className="row align-items-center g-3">
                <div className="col-12 col-lg-8">
                  <div className="card-title">Campanhas com Tickets Pagos</div>
                  <div className="card-category">
                    Campanhas em que você possui tickets com pagamento
                    confirmado.
                  </div>
                </div>
                <div className="col-12 col-lg-4">
                  <div className="d-flex flex-column flex-sm-row justify-content-lg-end gap-2">
                    <div style={{ minWidth: 150 }}>
                      <Select
                        animated
                        label="Registros"
                        className="form-select form-control"
                        aria-label="Quantidade de registros por página"
                        value={String(take)}
                        options={takeOptions}
                        onChange={(e) => {
                          setTake(Number(e.target.value));
                          setPage(1);
                        }}
                      />
                    </div>
                    <div style={{ minWidth: 150 }}>
                      <Select
                        animated
                        label="Ordenação"
                        className="form-select form-control"
                        aria-label="Ordenação da tabela"
                        value={order}
                        options={orderOptions}
                        onChange={(e) => {
                          setOrder(e.target.value as "DESC" | "ASC");
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive d-none d-lg-block">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col" className="text-start text-nowrap">
                        Campanha
                      </th>
                      <th scope="col" className="text-center text-nowrap">
                        Progresso
                      </th>
                      <th scope="col" className="text-center text-nowrap">
                        Tickets
                      </th>
                      <th scope="col" className="text-center text-nowrap">
                        Valores
                      </th>
                      <th scope="col" className="text-center text-nowrap">
                        Proprietário
                      </th>
                      <th scope="col" className="text-end text-nowrap">
                        Aquisição
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading.campaigns || campaigns.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-5">
                          <div className="fw-semibold">
                            {isLoading.campaigns
                              ? "Carregando campanhas..."
                              : "Nenhuma campanha encontrada."}
                          </div>
                          {!isLoading.campaigns && (
                            <small className="text-muted">
                              Ajuste os filtros para consultar outros tickets
                              pagos.
                            </small>
                          )}
                        </td>
                      </tr>
                    ) : (
                      campaigns.map((campaign) => {
                        const status = getRaffleStatus(campaign.status);
                        const type = getRaffleType(campaign.type);

                        return (
                          <tr key={campaign.key}>
                            <td
                              className="text-start"
                              style={{
                                minWidth: 380,
                                textAlign: "left",
                                verticalAlign: "middle",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "76px minmax(0, 1fr)",
                                  columnGap: 16,
                                  alignItems: "center",
                                  justifyItems: "start",
                                  textAlign: "left",
                                  width: "100%",
                                }}
                              >
                                <button
                                  type="button"
                                  className="border-0 bg-transparent p-0"
                                  title="Abrir thumb em zoom."
                                  onClick={() => setSelectedThumb(campaign)}
                                  style={{ justifySelf: "start" }}
                                >
                                  <img
                                    src={campaign.thumb}
                                    alt={campaign.name}
                                    style={{
                                      width: 76,
                                      height: 76,
                                      borderRadius: 8,
                                      objectFit: "cover",
                                      border: "1px solid #e5e7eb",
                                    }}
                                  />
                                </button>

                                <div
                                  style={{
                                    minWidth: 0,
                                    textAlign: "left",
                                    width: "100%",
                                    justifySelf: "stretch",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "inline-flex",
                                      flexWrap: "wrap",
                                      gap: 8,
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <span
                                      className={`badge bg-${status.color}`}
                                      title={status.title}
                                      style={{ borderRadius: 8 }}
                                    >
                                      {status.text}
                                    </span>
                                    <span
                                      className="badge bg-dark"
                                      title={type.title}
                                      style={{ borderRadius: 8 }}
                                    >
                                      {type.text}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0"
                                      title="Ver descrição curta."
                                      onClick={() =>
                                        setSelectedDescription(campaign)
                                      }
                                    >
                                      <FaCircleInfo className="fs-6" />
                                    </button>
                                  </div>

                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <Link
                                      to={`/campaign/default?campaign=${campaign.campaign}&key=${campaign.key}`}
                                      className="fw-bold text-dark text-decoration-none"
                                    >
                                      {campaign.name}
                                    </Link>
                                  </div>

                                  <div
                                    style={{
                                      display: "inline-flex",
                                      flexWrap: "wrap",
                                      gap: 8,
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      textAlign: "left",
                                    }}
                                  >
                                    <span
                                      className="badge bg-light text-muted border"
                                      title="Key da Campanha"
                                    >
                                      #{campaign.key}
                                    </span>
                                    <span
                                      className="badge bg-light text-muted border"
                                      title="Slug da Campanha"
                                    >
                                      #{campaign.campaign}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td
                              className="text-center"
                              style={{ minWidth: 170 }}
                            >
                              <div
                                className={`fw-bold text-${getProgressVariant(
                                  campaign.ticketSoldProgress,
                                )} mb-1`}
                                style={{ fontSize: "1rem" }}
                              >
                                {campaign.ticketSoldProgress.toFixed(2)}%
                              </div>
                              <small className="text-muted d-block mb-2">
                                {masks.formatMilhar(campaign.ticketSoldAmount)}{" "}
                                / {masks.formatMilhar(campaign.quantity)}
                              </small>
                              <div className="progress" style={{ height: 7 }}>
                                <div
                                  className={`progress-bar bg-${getProgressVariant(
                                    campaign.ticketSoldProgress,
                                  )}`}
                                  role="progressbar"
                                  style={{
                                    width: `${Math.min(
                                      campaign.ticketSoldProgress,
                                      100,
                                    )}%`,
                                  }}
                                  aria-valuenow={campaign.ticketSoldProgress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                />
                              </div>
                            </td>

                            <td
                              className="text-center"
                              style={{ minWidth: 150 }}
                            >
                              <div className="d-flex justify-content-center align-items-center gap-3">
                                <div>
                                  <small className="text-muted d-block">
                                    Meus
                                  </small>
                                  <span className="fw-bold">
                                    <FaTicket className="me-1 text-secondary" />
                                    {masks.formatMilhar(
                                      campaign.acquiredTickets,
                                    )}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    width: 1,
                                    height: 34,
                                    backgroundColor: "#e5e7eb",
                                  }}
                                />
                                <div>
                                  <small className="text-muted d-block">
                                    Total
                                  </small>
                                  <span className="fw-bold">
                                    {masks.formatMilhar(campaign.quantity)}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td
                              className="text-center"
                              style={{ minWidth: 190 }}
                            >
                              <div className="d-flex justify-content-center align-items-center gap-3">
                                <div>
                                  <span className="fw-bold d-block">
                                    {masks.brlCurrency(campaign.price)}
                                  </span>
                                  <small className="text-muted">Unitário</small>
                                </div>
                                <div
                                  className="px-3 py-2"
                                  style={{
                                    backgroundColor: "#e7f7e9",
                                    color: "#198754",
                                    borderRadius: 8,
                                  }}
                                >
                                  <span className="fw-bold d-block">
                                    {masks.brlCurrency(
                                      campaign.rewardPriceMarket,
                                    )}
                                  </span>
                                  <small>Prêmio total</small>
                                </div>
                              </div>
                            </td>

                            <td
                              className="text-center"
                              style={{ minWidth: 170 }}
                            >
                              <small className="d-block fw-semibold text-muted">
                                @{campaign.owner.atSign}
                              </small>
                              <small className="d-block text-muted">
                                {campaign.owner.name}
                              </small>
                            </td>

                            <td className="text-end" style={{ minWidth: 170 }}>
                              <div className="btn-group" role="group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-warning"
                                  title="Carregar tickets desta campanha"
                                  onClick={() =>
                                    handleCampaignTickets(campaign)
                                  }
                                >
                                  <FaTicket />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  title="Ver detalhes"
                                  onClick={() => setSelectedDetails(campaign)}
                                >
                                  <FaEye />
                                </button>
                                <Link
                                  to={`/campaign/default?campaign=${campaign.campaign}&key=${campaign.key}`}
                                  className="btn btn-sm btn-outline-success"
                                  title="Abrir campanha"
                                >
                                  <FaChartSimple />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-lg-none">
                <div className="mb-2">
                  <small className="fw-semibold text-muted">
                    Visualização em dispositivos móveis
                  </small>
                </div>

                <div className="row g-3">
                  {isLoading.campaigns || campaigns.length === 0 ? (
                    <div className="col-12 text-center py-4">
                      <div className="fw-semibold">
                        {isLoading.campaigns
                          ? "Carregando campanhas..."
                          : "Nenhuma campanha encontrada."}
                      </div>
                      {!isLoading.campaigns && (
                        <small className="text-muted">
                          Ajuste os filtros para consultar outros tickets pagos.
                        </small>
                      )}
                    </div>
                  ) : (
                    campaigns.map((campaign) => {
                      const status = getRaffleStatus(campaign.status);
                      const type = getRaffleType(campaign.type);

                      return (
                        <div className="col-12" key={`mobile-${campaign.key}`}>
                          <div
                            className="p-3 bg-white"
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: 8,
                              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "72px minmax(0, 1fr)",
                                columnGap: 12,
                                alignItems: "start",
                                justifyItems: "start",
                                textAlign: "left",
                                width: "100%",
                                marginBottom: 16,
                              }}
                            >
                              <button
                                type="button"
                                className="border-0 bg-transparent p-0"
                                title="Abrir thumb em zoom."
                                onClick={() => setSelectedThumb(campaign)}
                                style={{ justifySelf: "start" }}
                              >
                                <img
                                  src={campaign.thumb}
                                  alt={campaign.name}
                                  style={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 8,
                                    objectFit: "cover",
                                    border: "1px solid #e5e7eb",
                                  }}
                                />
                              </button>

                              <div
                                style={{
                                  minWidth: 0,
                                  textAlign: "left",
                                  width: "100%",
                                  justifySelf: "stretch",
                                }}
                              >
                                <div
                                  style={{
                                    display: "inline-flex",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    textAlign: "left",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    className={`badge bg-${status.color}`}
                                    style={{ borderRadius: 8 }}
                                    title={status.title}
                                  >
                                    {status.text}
                                  </span>
                                  <span
                                    className="badge bg-dark"
                                    style={{ borderRadius: 8 }}
                                    title={type.title}
                                  >
                                    {type.text}
                                  </span>
                                </div>
                                <Link
                                  to={`/campaign/default?campaign=${campaign.campaign}&key=${campaign.key}`}
                                  className="fw-bold text-dark text-decoration-none d-block"
                                  style={{ textAlign: "left" }}
                                >
                                  {campaign.name}
                                </Link>
                                <div
                                  style={{
                                    display: "inline-flex",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    textAlign: "left",
                                    marginTop: 8,
                                  }}
                                >
                                  <span className="badge bg-light text-muted border">
                                    #{campaign.key}
                                  </span>
                                  <span className="badge bg-light text-muted border">
                                    #{campaign.campaign}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="mb-2">
                              <div className="d-flex justify-content-between small mb-1">
                                <span
                                  className={`fw-bold text-${getProgressVariant(
                                    campaign.ticketSoldProgress,
                                  )}`}
                                >
                                  {campaign.ticketSoldProgress.toFixed(2)}%
                                </span>
                                <span className="text-muted">
                                  {masks.formatMilhar(
                                    campaign.ticketSoldAmount,
                                  )}{" "}
                                  / {masks.formatMilhar(campaign.quantity)}
                                </span>
                              </div>
                              <div className="progress" style={{ height: 6 }}>
                                <div
                                  className={`progress-bar bg-${getProgressVariant(
                                    campaign.ticketSoldProgress,
                                  )}`}
                                  role="progressbar"
                                  style={{
                                    width: `${Math.min(
                                      campaign.ticketSoldProgress,
                                      100,
                                    )}%`,
                                  }}
                                  aria-valuenow={campaign.ticketSoldProgress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                />
                              </div>
                            </div>

                            <div
                              className="d-flex align-items-center justify-content-between gap-2 border-top pt-2"
                              style={{ flexWrap: "wrap" }}
                            >
                              <div className="d-flex align-items-center gap-3">
                                <span className="fw-bold">
                                  <FaTicket className="me-1 text-secondary" />
                                  {masks.formatMilhar(campaign.acquiredTickets)}
                                </span>
                                <span className="fw-bold">
                                  {masks.brlCurrency(campaign.price)}
                                </span>
                                <span className="fw-bold text-success">
                                  {masks.brlCurrency(
                                    campaign.rewardPriceMarket,
                                  )}
                                </span>
                              </div>
                              <div className="btn-group" role="group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-warning"
                                  title="Carregar tickets desta campanha"
                                  onClick={() =>
                                    handleCampaignTickets(campaign)
                                  }
                                >
                                  <FaTicket />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  title="Ver detalhes"
                                  onClick={() => setSelectedDetails(campaign)}
                                >
                                  <FaEye />
                                </button>
                                <Link
                                  to={`/campaign/default?campaign=${campaign.campaign}&key=${campaign.key}`}
                                  className="btn btn-sm btn-outline-success"
                                  title="Abrir campanha"
                                >
                                  <FaChartSimple />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-md-center border-top mt-3 pt-3">
                <div>
                  <small className="text-muted">
                    Página {page} de {totalPages} página
                    {totalPages > 1 ? `s` : ``} ({masks.formatMilhar(total)}{" "}
                    registro
                    {total > 1 ? `s` : ``} encontrado{total > 1 ? `s` : ``})
                  </small>
                </div>

                <div>
                  <Pagination
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    maxPageShow={5}
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {selectedDescription && (
        <CampaignDescriptionModal
          campaign={selectedDescription}
          onClose={() => setSelectedDescription(undefined)}
        />
      )}

      {selectedDetails && (
        <CampaignDetailsModal
          campaign={selectedDetails}
          onClose={() => setSelectedDetails(undefined)}
        />
      )}

      {selectedThumb && (
        <ThumbLightbox
          campaign={selectedThumb}
          onClose={() => setSelectedThumb(undefined)}
        />
      )}

      {selectedTicketsCampaign && (
        <CampaignTicketsModal
          campaign={selectedTicketsCampaign}
          tickets={campaignTickets}
          total={totalTickets}
          totalPages={totalTicketPages}
          page={ticketPage}
          take={ticketTake}
          order={ticketOrder}
          setPage={setTicketPage}
          setTake={setTicketTake}
          setOrder={setTicketOrder}
          loading={isLoading.tickets}
          onClose={() => {
            setSelectedTicketsCampaign(undefined);
            setCampaignTickets([]);
            setTotalTickets(0);
            setTotalTicketPages(1);
          }}
        />
      )}
    </>
  );
}
